import { Icon, Markdown, Button, Spinner, Tooltip } from '@scale/scaleui';
import { InteractionStepTaskComponent } from '../../../InteractionStep';
import { CardAlert } from '../../../TempComponents';
import useChatCompletion from 'react-scale/hooks/chat/useChatCompletion';
import { useEffect, useState, useCallback, useMemo, useContext, memo } from 'react';
import { Skeleton } from '@mui/material';
import pluralize from 'pluralize';
import {
  InteractionStepType,
  DEFAULT_SEARCH_AUTOCOMPLETE_TITLE,
  DEFAULT_SEARCH_AUTOCOMPLETE_SUBTITLE,
  SearchResult,
  SearchResponse,
  MessageRole,
} from '@scale/shared/types/chat';
import { Transition } from '@headlessui/react';
import { BulbaBardEndpointOperation, CandidateState } from '@scale/shared/types/chat-completion';
import _ from 'lodash';
import { ChatTaskContext } from 'react-scale/components/scalers/tasks/ThreadedInteraction/contexts/ChatTaskContext';
import { SearchAutoCompleteStepResponse } from '@scale/shared/types/chat/response';

import {
  useChatStoreExtract,
  useStepParams,
  useStepResponse,
  useMostRecentPrompt,
  useChatHistory,
  useChatConfig,
} from '../../../../Chat/hooks/ChatStore.hooks';
import { useTrans } from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useTrans';
import { StepWidthWrapper, StepWidth } from '../../../step/StepWidthWrapper';
import { StepCard } from '../../../step/StepCard';
import { ResponseTemplatingButton } from '../../../step/ResponseTemplatingButton';
import { PinButton } from '../../../step/PinButton';
import { CommentButton } from '../../../feedback/CommentButton';
import { constructStepChildId } from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useWorkerCommentsStore';
import AdditionalReferencesSection from './AdditionalReferencesSection';
import { useStepSubmitHandler } from '../../../hooks/useStepSubmitHandler';
import useFetchSearchResults from './useFetchSearchResults';
import SearchResultsSection from './SearchResultsSection';
import { PopulatedProjectModelConfigDTO } from '@scale/customer-app-shared';

const SNIPPET_TOOLTIP = 'Please select at least one search snippet in each reference';
const SNIPPET_ADDITIONAL_REFERENCES_TOOLTIP =
  'Please select at least one search snippet in the main reference and one search snippet in an additional reference';
const NO_ADDITIONAL_REFERENCES_TOOLTIP = 'Please add at least one additional reference';
const INCOMPLETE_ADDITIONAL_REFERENCES_TOOLTIP =
  'Please select at least one search snippet in each additional reference or delete empty references';

const SearchAutoCompleteTaskComponent: InteractionStepTaskComponent =
  // eslint-disable-next-line complexity
  ({ readOnly, complete, responseIndex, isPinnedView }) => {
    const taskContext = useContext(ChatTaskContext);
    const { t } = useTrans();
    const config = useChatConfig();
    const chatHistory = useChatHistory(responseIndex);
    const chatPrompt = useMostRecentPrompt(responseIndex);
    const step = useStepParams<InteractionStepType.SearchAutoComplete>(responseIndex);
    const response = useStepResponse<SearchAutoCompleteStepResponse>(responseIndex);
    const { onSubmit } = useStepSubmitHandler({
      responseIndex,
    });
    const markStepAsIncompletedByIndex = useChatStoreExtract(
      state => state.markStepAsIncompletedByIndex,
    );
    const markStepAsCompletedByIndex = useChatStoreExtract(
      state => state.markStepAsCompletedByIndex,
    );
    const setProjectModelConfigurationMapping = useChatStoreExtract(
      state => state.setProjectModelConfigurationMapping,
    );

    const projectModelConfigurationMapping = useChatStoreExtract(
      state => state.projectModelConfigurationMapping,
    );

    const [submittedPrompt, setSubmittedPrompt] = useState<string | undefined>(undefined);

    const chatCompletionHook = useChatCompletion({
      chatApiEndpoints: step.params.chat_models,
      providedModelResponses: [],
      projectId: taskContext.projectId || null,
      useCase: 'chat-main search-auto-complete',
      options: {
        chatBenchmarkConfig: taskContext.chatBenchmarkConfig,
        templateVariables: config.templateVariables,
        projectModelConfigurationMapping,
      },
      stepId: step.id,
      responseIndex,
      setProjectModelConfigurationMapping: (
        projectModelConfigurationMapping: Record<string, PopulatedProjectModelConfigDTO>,
      ) => {
        setProjectModelConfigurationMapping(projectModelConfigurationMapping);
      },
    });

    // Note: Factuality endpoint will still need `model` to be specified.
    // Although the specified model will not be accessed, its factuality-related configuration will be applied to the search
    const searchModelId = step.params.chat_models?.[0]?.params?.model;

    // autocomplete is currently temporarily disabled
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [hasRunAutocomplete, setHasRunAutocomplete] = useState<boolean>(false);
    const candidates = useMemo(() => {
      if (!hasRunAutocomplete && response?.context?.candidates) {
        return response.context.candidates;
      }
      return chatCompletionHook.candidates;
    }, [chatCompletionHook.candidates, response?.context?.candidates, hasRunAutocomplete]);
    const candidateState = _.get(candidates, '[0].state');

    // Extract the relevant data from the existing response if it exists
    const [additionalReferences, setAdditionalReferences] = useState<SearchResponse[]>(
      response?.context?.additionalReferences ?? [],
    );

    const [searchResultsHistory, setSearchResultsHistory] = useState<SearchResponse[]>(
      response?.context?.searchHistory ?? [],
    );
    const [searchQuery, setSearchQuery] = useState<string>(
      _.last(searchResultsHistory)?.searchTerm ?? '',
    );
    const searchQueryResults = useMemo(() => {
      return _.last(searchResultsHistory)?.searchResults ?? [];
    }, [searchResultsHistory]);

    const areAdditionalReferencesComplete = useMemo(() => {
      return additionalReferences.every(
        additionalReference =>
          additionalReference.searchTerm.length > 0 &&
          additionalReference.searchResults.length > 0 &&
          _.some(additionalReference.searchResults, ['isChecked', true]),
      );
    }, [additionalReferences]);

    // Additional references may be optional, but at least one snippet in the main search query is always required
    const areAllSearchResultsValid = useMemo(() => {
      return (
        _.some(searchQueryResults, ['isChecked', true]) &&
        (step.params.make_additional_references_optional ||
          _.some(additionalReferences, additionalReference =>
            _.some(additionalReference.searchResults, ['isChecked', true]),
          ))
      );
    }, [additionalReferences, searchQueryResults, step.params.make_additional_references_optional]);

    const submitTooltip = useMemo(() => {
      if (!step.params.make_additional_references_optional && !additionalReferences.length) {
        return NO_ADDITIONAL_REFERENCES_TOOLTIP;
      }
      if (!areAdditionalReferencesComplete) {
        return INCOMPLETE_ADDITIONAL_REFERENCES_TOOLTIP;
      }
      if (!areAllSearchResultsValid) {
        return step.params.make_additional_references_optional
          ? SNIPPET_TOOLTIP
          : SNIPPET_ADDITIONAL_REFERENCES_TOOLTIP;
      }
      return '';
    }, [
      step.params.make_additional_references_optional,
      additionalReferences.length,
      areAdditionalReferencesComplete,
      areAllSearchResultsValid,
    ]);

    const processSearchResults = useCallback(
      (searchTerm: string, searchResults: SearchResult[]) => {
        const newSearchResults = searchResults.map((result: SearchResult) => ({
          ...result,
          isChecked: false,
        }));
        setSearchResultsHistory([
          ...searchResultsHistory,
          { searchTerm, searchResults: newSearchResults },
        ]);
      },
      [searchResultsHistory],
    );
    const [searchState, fetchSearchResults] = useFetchSearchResults(processSearchResults);

    // The first query and search results are returned by the model endpoint
    useEffect(() => {
      if (candidateState === CandidateState.Completed && !searchResultsHistory.length) {
        const responseSearchQuery = _.get(
          candidates[0],
          'additionalData.searchResults[0].query',
          '',
        );
        const responseSearchResults = candidates[0].additionalData?.searchResults?.map(
          (result: SearchResult) => ({
            ...result,
            isChecked: false,
          }),
        );
        if (!responseSearchQuery || !responseSearchResults) {
          return;
        }
        setSearchQuery(responseSearchQuery);
        setSearchResultsHistory([
          {
            searchTerm: responseSearchQuery,
            searchResults: responseSearchResults,
          },
        ]);
      }
    }, [candidates, searchResultsHistory, candidateState]);

    // Reactivate step if the current response differs from the existing response
    const resultChanged = useMemo(() => {
      return !_.isEqual(
        {
          candidates,
          searchHistory: searchResultsHistory,
          additionalReferences,
        },
        _.pick(response?.context, ['candidates', 'searchHistory', 'additionalReferences']),
      );
    }, [response, candidates, searchResultsHistory, additionalReferences]);

    /* Explicitly mark the step as incomplete / complete if an initial response
     * exists and the current response is different.
     */
    useEffect(() => {
      const canUpdateCompletedState = response && !_.isNil(responseIndex);
      if (canUpdateCompletedState && resultChanged) {
        markStepAsIncompletedByIndex(responseIndex);
      } else if (canUpdateCompletedState && !resultChanged) {
        markStepAsCompletedByIndex(responseIndex);
      }
    }, [
      response,
      resultChanged,
      responseIndex,
      markStepAsIncompletedByIndex,
      markStepAsCompletedByIndex,
    ]);

    // HACK - should come up with a better to accomplish this (same as ModelResponseSelector)
    useEffect(() => {
      if (complete) {
        return;
      }
      if (!chatPrompt || chatPrompt === submittedPrompt) {
        return;
      }
      if (response) {
        return;
      }

      async function validateAndSubmitPrompt() {
        setSubmittedPrompt(chatPrompt);
        const historyChopped = chatHistory.slice(0, -1); // TODO: @Lajos We should use the store here!
        if (config.systemPrompt?.length) {
          historyChopped.push({
            role: MessageRole.System,
            content: config.systemPrompt,
          });
        }
        chatCompletionHook.fetchCandidates(
          {
            role: MessageRole.User,
            content: chatPrompt,
          },
          historyChopped,
          config.systemPromptGptOnly,
        );
      }
      validateAndSubmitPrompt();
    }, [
      step.params,
      chatCompletionHook,
      chatPrompt,
      submittedPrompt,
      setSubmittedPrompt,
      chatHistory,
      complete,
      taskContext.assignmentId,
      taskContext.projectId,
      config.systemPrompt,
      config.systemPromptGptOnly,
      response,
    ]);

    const renderSpinner = !searchResultsHistory.length && candidateState === CandidateState.Loading;
    const renderSearchResultsSection =
      (!searchResultsHistory.length && candidateState === CandidateState.Completed) ||
      searchResultsHistory.length > 0;

    return (
      <StepWidthWrapper width={isPinnedView ? StepWidth.Full : StepWidth.Responsive}>
        <Transition
          show
          appear
          enter="transition-all duration-300"
          enterFrom="opacity-0 -translate-y-4"
          enterTo="opacity-100 translate-y-0 "
          leave="transition-all duration-150"
          leaveFrom="opacity-100 translate-y-0"
          leaveTo="opacity-0 -translate-y-4"
        >
          <CardAlert
            message={response && resultChanged ? 'Please save the section below.' : undefined}
          >
            <StepCard readOnly={readOnly}>
              <StepCard.Header>
                <StepCard.HeaderLeft>
                  <StepCard.Icon icon="award" />
                  <StepCard.HeaderColumn>
                    <StepCard.Title>
                      {step.title || DEFAULT_SEARCH_AUTOCOMPLETE_TITLE}
                    </StepCard.Title>
                    <StepCard.Subtitle>
                      {step.subtitle || DEFAULT_SEARCH_AUTOCOMPLETE_SUBTITLE}
                    </StepCard.Subtitle>
                  </StepCard.HeaderColumn>
                </StepCard.HeaderLeft>
                <StepCard.HeaderRight>
                  {!taskContext.enableUnifiedFeedback && (
                    <CommentButton
                      visible={true}
                      childId={constructStepChildId(step)}
                      turnIndex={step.turn}
                    />
                  )}
                  <ResponseTemplatingButton
                    childId={constructStepChildId(step)}
                    metadata={{ stepIndex: responseIndex }}
                  />
                  <PinButton responseIndex={responseIndex} />
                </StepCard.HeaderRight>
              </StepCard.Header>
              <StepCard.Body>
                {candidates.length > 0 && (
                  <div className="flex flex-col gap-4">
                    <StepCard readOnly={readOnly}>
                      <StepCard.Header>
                        <StepCard.HeaderLeft>
                          <StepCard.Subtitle>Model Response</StepCard.Subtitle>
                        </StepCard.HeaderLeft>
                      </StepCard.Header>
                      <StepCard.Body>
                        {candidateState === CandidateState.Loading && (
                          <>
                            <Markdown
                              className="text-sm"
                              markdown={candidates[0].message.content}
                              enableMath={taskContext.milkdownEditorConfig?.enableMathPlugin}
                              enableSingleDollarTextMath={
                                taskContext.milkdownEditorConfig?.enableSingleDollarTextMath
                              }
                              enableHtml={taskContext.htmlMarkdownConfig?.enableResponsePreviews}
                            />
                            <Skeleton width={4} />
                          </>
                        )}
                        {candidateState === CandidateState.Completed && (
                          <Markdown
                            className="text-sm"
                            markdown={candidates[0].message.content}
                            enableMath={taskContext.milkdownEditorConfig?.enableMathPlugin}
                            enableSingleDollarTextMath={
                              taskContext.milkdownEditorConfig?.enableSingleDollarTextMath
                            }
                            enableHtml={taskContext.htmlMarkdownConfig?.enableResponsePreviews}
                          />
                        )}
                        {candidateState === CandidateState.Error && (
                          <div className="flex flex-col items-center gap-2 p-3">
                            <div className="flex items-center text-danger-400">
                              <Icon variant="danger" size="sm" icon="exclamation-circle" />
                              Could not connect to the model.
                            </div>
                          </div>
                        )}
                      </StepCard.Body>
                      {candidateState === CandidateState.Error && (
                        <StepCard.Footer className="flex justify-end">
                          <Button
                            disabled={chatCompletionHook.refetchCandidateCooldown > 0}
                            variant="warning"
                            size="sm"
                            onClick={async () => {
                              const _chatHistory = [...chatHistory];
                              if (config.systemPrompt?.length) {
                                _chatHistory.push({
                                  role: MessageRole.System,
                                  content: config.systemPrompt,
                                });
                              }
                              await chatCompletionHook.fetchCandidate(
                                0,
                                {
                                  role: MessageRole.User,
                                  content: chatPrompt,
                                },
                                _chatHistory,
                                searchQueryResults.length > 0
                                  ? {
                                      search_results: searchQueryResults,
                                      operation: BulbaBardEndpointOperation.BARD_SEARCH_OVERRIDE,
                                      ignoreOldInteractionsInMessages: true,
                                    }
                                  : {},
                              );
                            }}
                          >
                            {chatCompletionHook.refetchCandidateCooldown > 0
                              ? `Please wait ${pluralize(
                                  'second',
                                  chatCompletionHook.refetchCandidateCooldown,
                                  true,
                                )}`
                              : 'Try Again'}
                          </Button>
                        </StepCard.Footer>
                      )}
                    </StepCard>
                    <div>
                      <div className="mb-1 text-sm font-medium text-neutral-700">References</div>
                      {renderSpinner && (
                        <div className="flex items-center justify-center">
                          <Spinner size="sm" variant="primary" />
                        </div>
                      )}
                      {renderSearchResultsSection && (
                        <div className="flex flex-col gap-2">
                          <SearchResultsSection
                            searchQuery={searchQuery}
                            setSearchQuery={setSearchQuery}
                            readOnly={!!readOnly}
                            searchQueryResults={searchQueryResults}
                            disableFetchSearchResults={
                              candidateState === CandidateState.Loading ||
                              !searchQuery.length ||
                              !!readOnly
                            }
                            searchState={searchState}
                            fetchSearchResults={fetchSearchResults}
                            onCheckboxChange={(index: number) => {
                              const updatedSearchResultsHistory = searchResultsHistory.map(
                                (historyResult, historyIndex) => {
                                  if (historyIndex === searchResultsHistory.length - 1) {
                                    const updatedResults = _.cloneDeep(searchQueryResults);
                                    updatedResults[index].isChecked =
                                      !updatedResults[index].isChecked;
                                    return {
                                      ...historyResult,
                                      searchResults: updatedResults,
                                    };
                                  }
                                  return historyResult;
                                },
                              );
                              setSearchResultsHistory(updatedSearchResultsHistory);
                            }}
                            resultIndex={1}
                            searchModelId={searchModelId}
                          />
                        </div>
                      )}
                    </div>
                    <div>
                      <AdditionalReferencesSection
                        additionalReferences={additionalReferences}
                        setAdditionalReferences={setAdditionalReferences}
                        readOnly={!!readOnly}
                        searchModelId={searchModelId}
                      />
                    </div>
                    <div className="flex gap-2 justify-start">
                      <Tooltip
                        text="Search for additional references"
                        side="top"
                        target={
                          <div>
                            <Button
                              onClick={() => {
                                setAdditionalReferences([
                                  ...additionalReferences,
                                  {
                                    searchTerm: '',
                                    searchResults: [],
                                  },
                                ]);
                              }}
                              disabled={
                                candidateState === CandidateState.Loading ||
                                !searchResultsHistory.length ||
                                readOnly
                              }
                              variant="secondary"
                            >
                              Add Additional Reference
                            </Button>
                          </div>
                        }
                      />
                      {
                        // currently disabled since customer's new endpoint does not support this functionality
                        // removing the button completely for now to not confuse contributors
                        /* <Tooltip
                      text="Regenerate the model response based on the current search query and results"
                      side="top"
                      target={
                        <div>
                          <Button
                            onClick={() => {
                              const historyChopped = chatHistory.slice(0, -1);
                              if (config.systemPrompt?.length) {
                                historyChopped.push({
                                  role: MessageRole.System,
                                  content: config.systemPrompt,
                                });
                              }
                              chatCompletionHook.fetchCandidates(
                                {
                                  role: MessageRole.User,
                                  content: chatPrompt,
                                },
                                historyChopped,
                                config.systemPromptGptOnly,
                                {
                                  search_results: searchQueryResults,
                                  operation: BulbaBardEndpointOperation.BARD_SEARCH_OVERRIDE,
                                  ignoreOldInteractionsInMessages: true,
                                },
                              );
                              const updatedSearchResultsHistory = _.cloneDeep(searchResultsHistory);
                              updatedSearchResultsHistory[
                                updatedSearchResultsHistory.length - 1
                              ].hasRunAutocomplete = true;
                              setSearchResultsHistory(updatedSearchResultsHistory);
                              setHasRunAutocomplete(true);
                            }}
                            disabled={
                              searchState.loading ||
                              !searchQueryResults.length ||
                              candidateState === CandidateState.Loading ||
                              !searchResultsHistory.length ||
                              readOnly
                            }
                            variant="primary"
                          >
                            Autocomplete
                          </Button>
                        </div>
                      }
                    /> */
                      }
                    </div>
                  </div>
                )}
              </StepCard.Body>
              {!taskContext.previewMode && (
                <StepCard.Footer className="flex gap-2 justify-end mt-4">
                  <Tooltip
                    text={submitTooltip}
                    side="top"
                    disabled={!submitTooltip}
                    target={
                      <div>
                        <Button
                          onClick={() => {
                            onSubmit(
                              {
                                type: InteractionStepType.SearchAutoComplete,
                                context: {
                                  prompt: chatPrompt,
                                  candidates,
                                  searchHistory: searchResultsHistory,
                                  additionalReferences,
                                },
                                output: candidates[0].message.content,
                              },
                              responseIndex,
                              step,
                              undefined,
                              // skip linting during submission
                              true,
                            );
                          }}
                          disabled={
                            candidateState === CandidateState.Loading ||
                            !searchResultsHistory.length ||
                            submitTooltip.length > 0 ||
                            !resultChanged ||
                            readOnly
                          }
                        >
                          {t('save-and-continue')}
                        </Button>
                      </div>
                    }
                  />
                </StepCard.Footer>
              )}
            </StepCard>
          </CardAlert>
        </Transition>
      </StepWidthWrapper>
    );
  };

export default memo(SearchAutoCompleteTaskComponent);
